import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type KeyboardEvent,
  type MouseEvent,
  type MutableRefObject,
  type SetStateAction,
} from 'react'
import type { INoteTranscriptSegment } from '@dataTypes/note.type'
import { mapTranscriptSegmentsToPanel } from './notesTranscript.util'
import type { UseNotesLiveTranscribeResult } from './useNotesLiveTranscribe'
import type {
  AiPreviewSource,
  LastNotesAiGeneration,
  NoteMicMode,
  NoteRecordingTake,
} from './noteEditor.types'
import { logger } from '@utils/safeLogger'
export type GenerateTranscriptSummary = (
  segments: INoteTranscriptSegment[],
  overrides?: { structureValue?: string; toneValue?: string },
) => Promise<void>

type UseNoteEditorRecordingParams = {
  liveTranscribe: UseNotesLiveTranscribeResult
  onViewTranscription?: () => void
  onTranscriptSegmentsChange?: (
    panelSegments: ReturnType<typeof mapTranscriptSegmentsToPanel>,
    persistable: INoteTranscriptSegment[],
  ) => void
  resetAIState: () => void
  setAiPreviewSource: Dispatch<SetStateAction<AiPreviewSource>>
  persistableTranscriptRef: MutableRefObject<INoteTranscriptSegment[]>
  transcriptSummaryRequestIdRef: MutableRefObject<number>
  transcriptSummarySegmentsRef: MutableRefObject<INoteTranscriptSegment[]>
  lastNotesAiGenerationRef: MutableRefObject<LastNotesAiGeneration | null>
  aiCommittedToNoteRef: MutableRefObject<boolean>
  pendingAutoReplaceInsertedAiRef: MutableRefObject<boolean>
  noteId: string | null
  savedTranscriptSegments: INoteTranscriptSegment[] | null | undefined
}

export type NoteEditorRecording = {
  isRecording: boolean
  isRecordingPaused: boolean
  recordingTakes: NoteRecordingTake[]
  activeTakeIndex: number
  activeMic: NoteMicMode
  setActiveMic: Dispatch<SetStateAction<NoteMicMode>>
  showResetRecordingModal: boolean
  flashModeTestUrl: string | null
  flashModeAudioRef: MutableRefObject<HTMLAudioElement | null>
  isSmartMode: boolean
  isFlashMode: boolean
  isLiveTranscribeConnecting: boolean
  isUpdatingTranscription: boolean
  isRecordingActive: boolean
  shouldShowAiRecordingBar: boolean
  hasRecordedAudio: boolean
  audioUrl: string | null
  shouldShowAiPlaybackControls: boolean
  showRecordingTakeNav: boolean
  isFlashModeTesting: boolean
  micStatusLabel: string
  micModeLabel: string
  generateTranscriptSummaryRef: MutableRefObject<GenerateTranscriptSummary | null>
  handleMicClick: (event: MouseEvent) => void
  handleRecordingCircleClick: (event: MouseEvent) => void
  handleRecordingStop: (event: MouseEvent) => void
  handleRecordingPause: (event: MouseEvent) => void
  handleResetRecordingRequest: (event?: MouseEvent | KeyboardEvent) => void
  handleConfirmResetRecording: () => void
  handleCancelResetRecording: () => void
  handlePreviousRecordingTake: () => void
  handleNextRecordingTake: () => void
  handleRecordingTakeEnded: () => void
}

export const useNoteEditorRecording = ({
  liveTranscribe,
  onViewTranscription,
  onTranscriptSegmentsChange,
  resetAIState,
  setAiPreviewSource,
  persistableTranscriptRef,
  transcriptSummaryRequestIdRef,
  transcriptSummarySegmentsRef,
  lastNotesAiGenerationRef,
  aiCommittedToNoteRef,
  pendingAutoReplaceInsertedAiRef,
  noteId,
  savedTranscriptSegments,
}: UseNoteEditorRecordingParams): NoteEditorRecording => {
  const [isRecording, setIsRecording] = useState(false)
  const [isRecordingPaused, setIsRecordingPaused] = useState(false)
  const [isFinalizingTranscript, setIsFinalizingTranscript] = useState(false)
  const [recordingTakes, setRecordingTakes] = useState<NoteRecordingTake[]>([])
  const [activeTakeIndex, setActiveTakeIndex] = useState(0)
  const [activeMic, setActiveMic] = useState<NoteMicMode>('standard')
  const [showResetRecordingModal, setShowResetRecordingModal] = useState(false)
  const [recordingDuration, setRecordingDuration] = useState(0)
  const [isFlashModeTesting, setIsFlashModeTesting] = useState(false)
  const [, setFlashModeTestBlob] = useState<Blob | null>(null)
  const [flashModeTestUrl, setFlashModeTestUrl] = useState<string | null>(null)
  const flashModeAudioRef = useRef<HTMLAudioElement | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const flashModeRecorderRef = useRef<MediaRecorder | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const flashModeStreamRef = useRef<MediaStream | null>(null)
  const recordingStartTimeRef = useRef<number>(0)
  const recordingDurationIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const flashModeTestTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const smartAudioChunksRef = useRef<Blob[]>([])
  const recordingTakesRef = useRef<NoteRecordingTake[]>([])
  const flashModeTestUrlRef = useRef<string | null>(null)
  const generateTranscriptSummaryRef = useRef<GenerateTranscriptSummary | null>(
    null,
  )
  const prevLoadedNoteIdRef = useRef<string | null | undefined>(undefined)

  const isSmartMode = activeMic === 'ai'
  const isFlashMode = activeMic === 'standard'
  const isLiveTranscribeConnecting = isSmartMode && liveTranscribe.isConnecting
  const isUpdatingTranscription =
    isSmartMode &&
    !isRecording &&
    (isFinalizingTranscript || liveTranscribe.isTranscribing)
  const isRecordingActive = isSmartMode && isRecording && !isRecordingPaused
  const shouldShowAiRecordingBar = isSmartMode
  const hasRecordedAudio = recordingTakes.length > 0
  const activeRecordingTake = recordingTakes[activeTakeIndex] ?? null
  const audioUrl = activeRecordingTake?.url ?? null
  const shouldShowAiPlaybackControls = isSmartMode && hasRecordedAudio
  const showRecordingTakeNav = recordingTakes.length > 1

  const micStatusLabel = useMemo(() => {
    if (isFlashMode) {
      if (isFlashModeTesting) return 'Testing microphone...'
      return 'Test microphone'
    }
    if (isLiveTranscribeConnecting) return 'Connecting...'
    if (isUpdatingTranscription) return 'Updating transcription...'
    if (!isRecording) return 'Ready to record'
    if (isRecordingPaused) return 'Recording paused'
    return 'Recording...'
  }, [
    isFlashMode,
    isFlashModeTesting,
    isLiveTranscribeConnecting,
    isRecording,
    isRecordingPaused,
    isUpdatingTranscription,
  ])
  const micModeLabel = activeMic === 'ai' ? 'Smart mode' : 'Flash mode'

  const startRecording = useCallback(async () => {
    const summarize = generateTranscriptSummaryRef.current
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      })
      mediaStreamRef.current = stream

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      })
      mediaRecorderRef.current = mediaRecorder
      smartAudioChunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          smartAudioChunksRef.current.push(event.data)
        }
      }

      let discardRecordingOnStop = false
      mediaRecorder.onstop = () => {
        liveTranscribe.stopSession()
        if (discardRecordingOnStop) {
          smartAudioChunksRef.current = []
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach((track) => track.stop())
            mediaStreamRef.current = null
          }
          setIsFinalizingTranscript(false)
          return
        }
        const audioBlob = new Blob(smartAudioChunksRef.current, {
          type: 'audio/webm',
        })
        let takeIndex = -1
        if (audioBlob.size > 0) {
          const url = URL.createObjectURL(audioBlob)
          const take: NoteRecordingTake = {
            id: `take-${Date.now()}-${recordingTakesRef.current.length}`,
            url,
            blob: audioBlob,
          }
          const nextTakes = [...recordingTakesRef.current, take]
          takeIndex = nextTakes.length - 1
          recordingTakesRef.current = nextTakes
          setRecordingTakes(nextTakes)
          setActiveTakeIndex(takeIndex)
        }

        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach((track) => track.stop())
          mediaStreamRef.current = null
        }

        if (audioBlob.size > 0 && takeIndex >= 0) {
          void (async () => {
            try {
              const { allSegments, takeSegments } =
                await liveTranscribe.finalizeWithDiarize(audioBlob)
              const currentTakes = [...recordingTakesRef.current]
              if (currentTakes[takeIndex]) {
                currentTakes[takeIndex] = {
                  ...currentTakes[takeIndex],
                  segments: takeSegments,
                }
                recordingTakesRef.current = currentTakes
                setRecordingTakes(currentTakes)
              }
              persistableTranscriptRef.current = allSegments
              // Panel timestamps must match the active take's audio (0-based).
              onTranscriptSegmentsChange?.(
                mapTranscriptSegmentsToPanel(takeSegments),
                allSegments,
              )
              if (summarize) {
                await summarize(
                  allSegments.length > 0 ? allSegments : takeSegments,
                )
              }
            } finally {
              setIsFinalizingTranscript(false)
            }
          })()
        } else {
          setIsFinalizingTranscript(false)
        }
      }

      // Start recorder first so live transcript timestamps share the same t=0.
      mediaRecorder.start()
      recordingStartTimeRef.current = Date.now()
      setRecordingDuration(0)
      recordingDurationIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor(
          (Date.now() - recordingStartTimeRef.current) / 1000,
        )
        setRecordingDuration(elapsed)
      }, 1000)
      setIsRecording(true)
      setIsRecordingPaused(false)

      onViewTranscription?.()
      try {
        await liveTranscribe.startSession(stream, {
          listeningStartedAt: recordingStartTimeRef.current,
        })
      } catch (voiceError) {
        logger.error('Notes live voice session failed:', voiceError)
        discardRecordingOnStop = true
        if (mediaRecorderRef.current?.state !== 'inactive') {
          mediaRecorderRef.current?.stop()
        }
        mediaRecorderRef.current = null
        if (recordingDurationIntervalRef.current) {
          clearInterval(recordingDurationIntervalRef.current)
          recordingDurationIntervalRef.current = null
        }
        setIsRecording(false)
        setIsRecordingPaused(false)
        mediaStreamRef.current?.getTracks().forEach((track) => track.stop())
        mediaStreamRef.current = null
        const reason =
          voiceError instanceof Error
            ? voiceError.message
            : 'Unable to start live transcription.'
        alert(reason)
        return
      }
    } catch (error) {
      logger.error('Error starting recording:', error)
      alert('Failed to start recording. Please check microphone permissions.')
    }
  }, [
    liveTranscribe,
    onTranscriptSegmentsChange,
    onViewTranscription,
    persistableTranscriptRef,
  ])

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      setIsFinalizingTranscript(true)
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      setIsRecordingPaused(false)

      if (recordingDurationIntervalRef.current) {
        clearInterval(recordingDurationIntervalRef.current)
        recordingDurationIntervalRef.current = null
      }
    }
  }, [isRecording])

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && !isRecordingPaused) {
      mediaRecorderRef.current.pause()
      liveTranscribe.setPaused(true)
      setIsRecordingPaused(true)

      if (recordingDurationIntervalRef.current) {
        clearInterval(recordingDurationIntervalRef.current)
        recordingDurationIntervalRef.current = null
      }
    }
  }, [isRecording, isRecordingPaused, liveTranscribe])

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecordingPaused) {
      mediaRecorderRef.current.resume()
      liveTranscribe.setPaused(false)
      setIsRecordingPaused(false)
      recordingStartTimeRef.current = Date.now() - recordingDuration * 1000

      recordingDurationIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor(
          (Date.now() - recordingStartTimeRef.current) / 1000,
        )
        setRecordingDuration(elapsed)
      }, 1000)
    }
  }, [isRecordingPaused, recordingDuration, liveTranscribe])

  const startFlashModeTest = useCallback(async () => {
    if (isFlashModeTesting) return

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      flashModeStreamRef.current = stream

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      })
      flashModeRecorderRef.current = mediaRecorder

      const audioChunks: Blob[] = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' })
        setFlashModeTestBlob(audioBlob)
        if (flashModeTestUrlRef.current) {
          URL.revokeObjectURL(flashModeTestUrlRef.current)
        }
        const url = URL.createObjectURL(audioBlob)
        flashModeTestUrlRef.current = url
        setFlashModeTestUrl(url)
        setIsFlashModeTesting(false)

        if (flashModeStreamRef.current) {
          flashModeStreamRef.current
            .getTracks()
            .forEach((track) => track.stop())
          flashModeStreamRef.current = null
        }

        if (flashModeAudioRef.current && url) {
          flashModeAudioRef.current.src = url
          flashModeAudioRef.current.play().catch((error) => {
            logger.error('Error playing test audio:', error)
          })
        }
      }

      mediaRecorder.start()
      setIsFlashModeTesting(true)

      flashModeTestTimeoutRef.current = setTimeout(() => {
        if (
          flashModeRecorderRef.current &&
          flashModeRecorderRef.current.state === 'recording'
        ) {
          flashModeRecorderRef.current.stop()
        }
      }, 5000)
    } catch (error) {
      logger.error('Error starting mic test:', error)
      alert(
        'Failed to start microphone test. Please check microphone permissions.',
      )
      setIsFlashModeTesting(false)
    }
  }, [isFlashModeTesting])

  const stopFlashModeTest = useCallback(() => {
    if (flashModeRecorderRef.current && isFlashModeTesting) {
      flashModeRecorderRef.current.stop()
      setIsFlashModeTesting(false)

      if (flashModeTestTimeoutRef.current) {
        clearTimeout(flashModeTestTimeoutRef.current)
        flashModeTestTimeoutRef.current = null
      }
    }
  }, [isFlashModeTesting])

  const handleMicClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (
        target.closest('button') ||
        target.closest('.notes-mic-status-circle') ||
        target.closest('.notes-mic-status-square') ||
        target.closest('.notes-mic-status-pause')
      ) {
        return
      }

      if (isSmartMode) {
        if (isUpdatingTranscription) return
        if (isRecording) {
          stopRecording()
        } else {
          startRecording()
        }
      } else if (isFlashMode) {
        if (isFlashModeTesting) {
          stopFlashModeTest()
        } else {
          startFlashModeTest()
        }
      }
    },
    [
      isRecording,
      isSmartMode,
      isFlashMode,
      isFlashModeTesting,
      isUpdatingTranscription,
      startRecording,
      stopRecording,
      startFlashModeTest,
      stopFlashModeTest,
    ],
  )

  const handleRecordingCircleClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      if (isSmartMode) {
        if (isUpdatingTranscription) return
        if (!isRecording) {
          startRecording()
          return
        }

        if (isRecordingPaused) {
          resumeRecording()
        }
      } else if (isFlashMode) {
        if (isFlashModeTesting) {
          stopFlashModeTest()
        } else {
          startFlashModeTest()
        }
      }
    },
    [
      isRecording,
      isRecordingPaused,
      isSmartMode,
      isFlashMode,
      isFlashModeTesting,
      isUpdatingTranscription,
      startRecording,
      resumeRecording,
      startFlashModeTest,
      stopFlashModeTest,
    ],
  )

  const handleRecordingStop = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      if (isSmartMode) {
        if (!isRecording) return
        stopRecording()
      } else if (isFlashMode) {
        if (!isFlashModeTesting) return
        stopFlashModeTest()
      }
    },
    [
      isRecording,
      isSmartMode,
      isFlashMode,
      isFlashModeTesting,
      stopRecording,
      stopFlashModeTest,
    ],
  )

  const handleRecordingPause = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      if (!isSmartMode) return
      if (!isRecording || isRecordingPaused) return
      pauseRecording()
    },
    [isRecording, isRecordingPaused, isSmartMode, pauseRecording],
  )

  const handleResetRecordingRequest = useCallback(
    (event?: MouseEvent | KeyboardEvent) => {
      event?.stopPropagation()
      if (!hasRecordedAudio) return
      setShowResetRecordingModal(true)
    },
    [hasRecordedAudio],
  )

  const handleConfirmResetRecording = useCallback(() => {
    recordingTakesRef.current.forEach((take) => {
      URL.revokeObjectURL(take.url)
    })
    recordingTakesRef.current = []
    setRecordingTakes([])
    setActiveTakeIndex(0)
    setRecordingDuration(0)
    setShowResetRecordingModal(false)
    smartAudioChunksRef.current = []
    transcriptSummaryRequestIdRef.current += 1
    transcriptSummarySegmentsRef.current = []
    lastNotesAiGenerationRef.current = null
    aiCommittedToNoteRef.current = false
    pendingAutoReplaceInsertedAiRef.current = false
    setAiPreviewSource(null)
    resetAIState()
    liveTranscribe.reset()
  }, [
    aiCommittedToNoteRef,
    lastNotesAiGenerationRef,
    liveTranscribe,
    pendingAutoReplaceInsertedAiRef,
    resetAIState,
    setAiPreviewSource,
    transcriptSummaryRequestIdRef,
    transcriptSummarySegmentsRef,
  ])

  const handlePreviousRecordingTake = useCallback((): void => {
    setActiveTakeIndex((index) => Math.max(0, index - 1))
  }, [])

  const handleNextRecordingTake = useCallback((): void => {
    setActiveTakeIndex((index) =>
      Math.min(recordingTakesRef.current.length - 1, index + 1),
    )
  }, [])

  const handleRecordingTakeEnded = useCallback((): void => {
    setActiveTakeIndex((index) => {
      const lastIndex = recordingTakesRef.current.length - 1
      return index < lastIndex ? index + 1 : index
    })
  }, [])

  const handleCancelResetRecording = useCallback(() => {
    setShowResetRecordingModal(false)
  }, [])

  useEffect(() => {
    if (!isRecording) {
      setIsRecordingPaused(false)
    }
  }, [isRecording])

  useEffect(() => {
    if (isRecording) return
    const take = recordingTakes[activeTakeIndex]
    if (!take?.segments?.length) return
    onTranscriptSegmentsChange?.(
      mapTranscriptSegmentsToPanel(take.segments),
      persistableTranscriptRef.current,
    )
  }, [
    activeTakeIndex,
    isRecording,
    onTranscriptSegmentsChange,
    persistableTranscriptRef,
    recordingTakes,
  ])

  useEffect(() => {
    if (!isSmartMode) {
      if (isRecording && mediaRecorderRef.current) {
        stopRecording()
      }
    }
  }, [isSmartMode, isRecording, stopRecording])

  useEffect(() => {
    const previousNoteId = prevLoadedNoteIdRef.current
    prevLoadedNoteIdRef.current = noteId

    if (previousNoteId != null && previousNoteId !== noteId) {
      recordingTakesRef.current.forEach((take) => {
        URL.revokeObjectURL(take.url)
      })
      recordingTakesRef.current = []
      setRecordingTakes([])
      setActiveTakeIndex(0)
    }

    if (
      Array.isArray(savedTranscriptSegments) &&
      savedTranscriptSegments.length > 0
    ) {
      liveTranscribe.loadSavedSegments(savedTranscriptSegments)
      persistableTranscriptRef.current = savedTranscriptSegments
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per note id
  }, [noteId])

  useEffect(() => {
    return () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop())
        mediaStreamRef.current = null
      }

      if (recordingDurationIntervalRef.current) {
        clearInterval(recordingDurationIntervalRef.current)
      }

      recordingTakesRef.current.forEach((take) => {
        URL.revokeObjectURL(take.url)
      })
      recordingTakesRef.current = []

      if (flashModeTestUrlRef.current) {
        URL.revokeObjectURL(flashModeTestUrlRef.current)
        flashModeTestUrlRef.current = null
      }

      if (flashModeStreamRef.current) {
        flashModeStreamRef.current.getTracks().forEach((track) => track.stop())
        flashModeStreamRef.current = null
      }

      if (flashModeTestTimeoutRef.current) {
        clearTimeout(flashModeTestTimeoutRef.current)
      }
    }
  }, [])

  return {
    isRecording,
    isRecordingPaused,
    recordingTakes,
    activeTakeIndex,
    activeMic,
    setActiveMic,
    showResetRecordingModal,
    flashModeTestUrl,
    flashModeAudioRef,
    isSmartMode,
    isFlashMode,
    isLiveTranscribeConnecting,
    isUpdatingTranscription,
    isRecordingActive,
    shouldShowAiRecordingBar,
    hasRecordedAudio,
    audioUrl,
    shouldShowAiPlaybackControls,
    showRecordingTakeNav,
    isFlashModeTesting,
    micStatusLabel,
    micModeLabel,
    generateTranscriptSummaryRef,
    handleMicClick,
    handleRecordingCircleClick,
    handleRecordingStop,
    handleRecordingPause,
    handleResetRecordingRequest,
    handleConfirmResetRecording,
    handleCancelResetRecording,
    handlePreviousRecordingTake,
    handleNextRecordingTake,
    handleRecordingTakeEnded,
  }
}
